<script>
  import { createEventDispatcher } from 'svelte';
  import Input from '@components/Input.svelte';
  import Button from '@components/Button.svelte';
  import Avatar from '@components/Avatar.svelte';
  import Auth from '@utils/auth';
  import Notification from '@utils/notification';
  import UserAjax from '@ajax/user';
  import { getCurrentUserStore } from '@stores/current_user';

  const dispatch = createEventDispatcher();

  export let mobile = false;
  export let inviteUser;
  export let showInvitedUserBanner = true;
  export let prefilledUsername = null;

  let usernameEl;
  let usernameValue = prefilledUsername;
  let loadingUsername = false;
  let usernameInvalidMessage;
  let currentUser = getCurrentUserStore('default');

  $: enabledUsernameNextButton = usernameValue && usernameValue !== '' && !usernameInvalidMessage;

  let updateCurrentUserUsername = async function () {
    try {
      let updatedUser = await UserAjax.update(currentUser, { username: usernameEl.value });

      dispatch('finished', { user: updatedUser });
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
      }
    }
  };

  // we should load these up at this step so they are ready by the time congrats step is reached.
  let loadCongratsImages = function () {
    new Image().src = '/images/app-invites/party.svg';
    new Image().src = '/images/app-invites/clapping-hands.svg';
    new Image().src = '/images/app-invites/smiling-face.png';
    new Image().src = '/images/app-invites/smiling-face@2x.png';
  };

  let validateUsername = function () {
    let username = usernameEl.value;

    if (!Auth.validUsername(username)) {
      usernameInvalidMessage =
        'Username can only contain letters, numbers or underscore. (must start with a letter or number)';
    } else {
      usernameInvalidMessage = undefined;
    }
  };

  let checkUsername = async function () {
    if (!enabledUsernameNextButton) {
      return;
    }

    validateUsername();

    if (usernameInvalidMessage) {
      return;
    }

    loadingUsername = true;

    try {
      let resp = await UserAjax.checkUsername(usernameEl.value);

      if (resp) {
        usernameInvalidMessage = 'Username is taken. Please select another one';
      } else {
        await updateCurrentUserUsername();
      }
    } catch {}

    loadingUsername = false;
  };
</script>

<style style="text/scss" lang="scss">
  @import '../../../vendor/design-system.scss';
  .reverse-username {
    margin-top: 30px;

    .ui-title {
      font-size: 32px;
      line-height: 40px;
      text-align: center;
      font-weight: bold;
      margin-top: 32px;
    }

    .ui-subtitle {
      font-size: 16px;
      line-height: 24px;
      text-align: center;
      color: #7f7f7f;
      margin-top: 16px;
    }

    .input-container {
      margin-top: 32px;
      width: 380px;
      margin-left: auto;
      margin-right: auto;
      text-align: left;
      position: relative;

      .at_symbol {
        position: absolute;
        z-index: 1;
        top: 14px;
        left: 14px;
      }
    }

    .button-container {
      position: absolute;
      bottom: 64px;
      width: 180px;
      left: calc(50% - 90px);
    }
  }

  .icon {
    width: 64px;
    height: 64px;
    &.hide {
      display: none;
    }
  }

  .mobile {
    padding-top: 0px;
    padding: 10px;
    .bonus-banner-container {
      padding-bottom: 0px;
    }

    &.reverse-username {
      margin-top: auto;
    }

    .input-container {
      width: 100%;
      margin-top: 28px;
    }

    .button-container {
      position: unset !important;
      margin-left: auto;
      margin-right: auto;
      margin-top: 30px;
    }

    .invited-user-header {
      .top-row {
        display: block;
      }

      .avatar-wrapper {
        margin-bottom: 10px;
      }

      .text {
        margin-left: 0px;

        display: block;

        .name {
          margin-right: 2px;
        }
      }
    }
  }

  .invited-user-header {
    max-width: 650px;
    margin: 0 auto;
    border-bottom: 1px solid $neutral-input-gray;
    margin-bottom: 25px;
    color: $neutral-gray;

    .top-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
    }

    .text {
      display: flex;
      margin-left: 12px;
      margin-bottom: 0px;

      .name {
        color: $modal-black;
        margin-right: 4px;
        font-weight: $font-weight-bold;
      }
    }

    .bonus {
      padding: 4px 12px;
      background: #e7f9f3;
      border-radius: 16px;
      font-weight: $font-weight-semi-bold;
      color: #2caf87;
      white-space: nowrap;
    }

    .bold {
      font-weight: 600;
    }
  }
  .top-row-container {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .bonus-banner-container {
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    /* or 143% */
    text-align: center;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    max-width: 350px;

    color: $neutral-gray;
    margin-bottom: 24px;

    /* Inside auto layout */
    flex: none;
    order: 2;
    flex-grow: 0;
    .ui-bonus-percentage {
      font-weight: $font-weight-bold;
      color: #18906c;
    }
    .ui-bonus-days {
      color: $modal-black;
      font-weight: $font-weight-bold;
    }
  }
</style>

<div class="reverse-username" class:mobile={mobile} use:loadCongratsImages>
  {#if inviteUser && showInvitedUserBanner}
    <div class="invited-user-header">
      <div class="top-row-container">
        <div class="top-row">
          <div class="avatar-wrapper">
            <Avatar width={48} src={inviteUser.avatar_url} user={inviteUser} />
          </div>
          <p class="text">
            <span class="name">{inviteUser.name}</span>
            has invited you to join Benable!
          </p>
        </div>
        <div class="bonus-banner-container {mobile ? 'mobile' : ''}">
          <div class="ui-bonus-header">
            Thanks to {inviteUser.name}, you’re earning a <span class="ui-bonus-percentage">10% bonus</span> on your
            commissions for the <span class="ui-bonus-days">next 30 days!</span>
          </div>
        </div>
      </div>
    </div>
  {/if}
  <img class="icon {mobile && inviteUser ? 'hide' : ''}" alt="sparkles" src="/images/app-invites/sparkles.svg" />

  <div class="ui-title">Reserve your Benable username</div>
  <div class="ui-subtitle">You can always change it later</div>

  <div class="input-container">
    <div class="at_symbol">@</div>
    <Input
      bind:value={usernameValue}
      bind:inputEl={usernameEl}
      on:keydown={(e) => {
        if (e.detail.which === 32) {
          e.detail.event.preventDefault();
        }
      }}
      on:keyup={(e) => {
        if (e.detail.which === 13) {
          checkUsername();
        } else {
          validateUsername();
        }
      }}
      placeholder="username"
      focus={true}
      loading={loadingUsername}
      error={usernameInvalidMessage}
      inputStyle="padding-left: 29px;" />
  </div>

  <div class="button-container">
    {#if loadingUsername}
      <Button disabled={true}><i class="fa fa-spinner fa-spin" /></Button>
    {:else}
      <Button on:click={checkUsername} disabled={!enabledUsernameNextButton} text="Next" />
    {/if}
  </div>
</div>
